import React, { useEffect, useState } from 'react';
import { ArrowLeft, ShieldCheck, Smartphone } from 'lucide-react';
import api from '../lib/api';
import FeatureHeader from './FeatureHeader';

export default function SecurityPrivacy({ onBack }) {
  const [bioLock, setBioLock] = useState(true);
  const [reminderLogin, setReminderLogin] = useState(false);
  const [dataSharing, setDataSharing] = useState(true);
  const [hasPasscode, setHasPasscode] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const [passcode, setPasscode] = useState('');
  const [confirmPasscode, setConfirmPasscode] = useState('');

  const [showResetModal, setShowResetModal] = useState(false);
  const [biometricStatus, setBiometricStatus] = useState('Checking...');
  const [deviceSyncStatus, setDeviceSyncStatus] = useState('Checking linked devices...');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState({ type: '', message: '' });

  const showStatus = (type, message) => {
    setStatus({ type, message });
    if (type === 'success') {
      window.setTimeout(() => {
        setStatus((prev) => (prev.message === message ? { type: '', message: '' } : prev));
      }, 2600);
    }
  };

  useEffect(() => {
    const checkBiometric = async () => {
      if (!window.PublicKeyCredential) {
        setBiometricStatus('Biometric authentication not supported on this device/browser');
        return;
      }

      try {
        const available = await window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable();
        setBiometricStatus(
          available
            ? 'Biometric authentication available'
            : 'Biometric authentication not available on this device'
        );
      } catch {
        setBiometricStatus('Unable to detect biometric status');
      }
    };

    checkBiometric();
  }, []);

  useEffect(() => {
    const loadSecurity = async () => {
      setLoading(true);
      try {
        const [securityRes, devicesRes] = await Promise.all([
          api.get('/api/security-settings'),
          api.get('/api/linked-devices').catch(() => ({ data: { devices: [] } })),
        ]);

        const settings = securityRes.data?.settings || {};
        setBioLock(Boolean(settings.bioLock));
        setReminderLogin(Boolean(settings.reminderLogin));
        setDataSharing(Boolean(settings.dataSharing));
        setHasPasscode(Boolean(settings.hasPasscode));

        const devices = devicesRes.data?.devices || [];
        if (!devices.length) {
          setDeviceSyncStatus('No linked devices yet');
        } else {
          const latest = devices
            .map((d) => d.lastSyncedAt)
            .filter(Boolean)
            .sort((a, b) => new Date(b) - new Date(a))[0];

          if (!latest) {
            setDeviceSyncStatus(`${devices.length} device(s) linked`);
          } else {
            setDeviceSyncStatus(`Last sync: ${new Date(latest).toLocaleString()}`);
          }
        }
      } catch {
        showStatus('error', 'Failed to load security settings');
      } finally {
        setLoading(false);
      }
    };

    loadSecurity();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/api/security-settings', {
        bioLock,
        reminderLogin,
        dataSharing,
      });
      showStatus('success', 'Security preferences saved');
    } catch (err) {
      showStatus('error', err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword || !newPassword) {
      showStatus('error', 'Please fill current and new password');
      return;
    }
    if (newPassword.length < 8) {
      showStatus('error', 'New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      showStatus('error', 'Password confirmation does not match');
      return;
    }

    try {
      await api.post('/api/security-settings/change-password', {
        currentPassword,
        newPassword,
      });
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showStatus('success', 'Password updated successfully');
    } catch (err) {
      showStatus('error', err?.response?.data?.message || 'Failed to change password');
    }
  };

  const handleUpdatePasscode = async () => {
    if (!passcode) {
      showStatus('error', 'Passcode is required');
      return;
    }
    if (!/^\d{4,8}$/.test(passcode)) {
      showStatus('error', 'Passcode must be 4 to 8 digits');
      return;
    }
    if (passcode !== confirmPasscode) {
      showStatus('error', 'Passcode confirmation does not match');
      return;
    }

    try {
      const res = await api.post('/api/security-settings/passcode', { passcode });
      setHasPasscode(Boolean(res.data?.hasPasscode));
      setPasscode('');
      setConfirmPasscode('');
      showStatus('success', 'Passcode updated successfully');
    } catch (err) {
      showStatus('error', err?.response?.data?.message || 'Failed to update passcode');
    }
  };

  const handleReset = async () => {
    setShowResetModal(false);
    try {
      const res = await api.post('/api/security-settings/reset');
      const settings = res.data?.settings || {};
      setBioLock(Boolean(settings.bioLock));
      setReminderLogin(Boolean(settings.reminderLogin));
      setDataSharing(Boolean(settings.dataSharing));
      setHasPasscode(Boolean(settings.hasPasscode));
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasscode('');
      setConfirmPasscode('');
      showStatus('success', 'Security settings reset');
    } catch (err) {
      showStatus('error', err?.response?.data?.message || 'Failed to reset settings');
    }
  };

  return (
    <div style={pageStyle}>
      <FeatureHeader title = "Security & Privacy" onBack={onBack} />

      <div style={infoCard}>
        <ShieldCheck size={24} style={{ color: 'var(--primary)' }} />
        <div>
          <div style={{ fontWeight: '600' }}>Biometric Status</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>{biometricStatus}</div>
        </div>
      </div>

      <div style={infoCard}>
        <Smartphone size={24} style={{ color: 'var(--primary)' }} />
        <div>
          <div style={{ fontWeight: '600' }}>Device Sync</div>
          <div style={{ fontSize: '0.75rem', color: '#666' }}>{deviceSyncStatus}</div>
        </div>
      </div>

      {loading ? (
        <div style={{ fontSize: '0.9rem', color: '#666' }}>Loading security settings...</div>
      ) : (
        <>
          <div style={sectionCard}>
            <div style={sectionTitle}>Privacy Preferences</div>

            <label style={toggleRow}>
              <span>Enable Biometric Lock</span>
              <input type="checkbox" checked={bioLock} onChange={() => setBioLock(!bioLock)} />
            </label>

            <label style={toggleRow}>
              <span>Require Login Reminder</span>
              <input type="checkbox" checked={reminderLogin} onChange={() => setReminderLogin(!reminderLogin)} />
            </label>

            <label style={toggleRow}>
              <span>Allow Data Sharing for Care Team</span>
              <input type="checkbox" checked={dataSharing} onChange={() => setDataSharing(!dataSharing)} />
            </label>

            <button onClick={handleSave} style={buttonStyle} disabled={saving}>
              {saving ? 'Saving...' : 'Save Security Preferences'}
            </button>
          </div>

          <div style={sectionCard}>
            <div style={sectionTitle}>Change Password</div>
            <input
              type="password"
              placeholder="Current password"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="New password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              style={inputStyle}
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              style={inputStyle}
            />
            <button onClick={handleChangePassword} style={buttonStyle}>Update Password</button>
          </div>

          <div style={sectionCard}>
            <div style={sectionTitle}>Passcode</div>
            <div style={{ fontSize: '0.78rem', color: '#666', marginBottom: 10 }}>
              {hasPasscode ? 'Passcode is currently set.' : 'No passcode set yet.'}
            </div>
            <input
              type="password"
              inputMode="numeric"
              placeholder="New passcode (4-8 digits)"
              value={passcode}
              onChange={(e) => setPasscode(e.target.value)}
              style={inputStyle}
            />
            <input
              type="password"
              inputMode="numeric"
              placeholder="Confirm passcode"
              value={confirmPasscode}
              onChange={(e) => setConfirmPasscode(e.target.value)}
              style={inputStyle}
            />
            <button onClick={handleUpdatePasscode} style={buttonStyle}>Update Passcode</button>
          </div>

          <button onClick={() => setShowResetModal(true)} style={resetBtnStyle}>Clear & Reset Security Settings</button>
        </>
      )}

      {status.message && (
        <div style={{ ...statusStyle, color: status.type === 'error' ? '#b42318' : '#15803d' }}>
          {status.message}
        </div>
      )}

      {showResetModal && (
        <div style={modalOverlay}>
          <div style={modalCard}>
            <h2 style={{ fontSize: '1rem', marginBottom: 8 }}>Confirm Reset</h2>
            <p style={{ fontSize: '0.85rem', marginBottom: 16 }}>
              This will reset biometric/login/sharing preferences and remove your saved passcode.
            </p>
            <div style={{ display: 'flex', gap: 12 }}>
              <button onClick={handleReset} style={buttonStyle}>Yes, Reset</button>
              <button onClick={() => setShowResetModal(false)} style={cancelBtnStyle}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const pageStyle = {
  minHeight: '100vh',
  padding: '24px 16px',
  background: 'var(--bg)',
  fontFamily: 'sans-serif',
  color: 'var(--on-bg)',
};

const headerStyle = {
  fontSize: '1.25rem',
  fontWeight: '700',
};

const backBtnStyle = {
  background: 'transparent',
  border: 'none',
  padding: 0,
  marginRight: 8,
  cursor: 'pointer',
};

const infoCard = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginBottom: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const sectionCard = {
  background: 'var(--surface)',
  borderRadius: 12,
  padding: 16,
  marginTop: 12,
  boxShadow: '0 1px 4px rgba(0,0,0,0.05)',
};

const sectionTitle = {
  fontSize: '0.95rem',
  fontWeight: 600,
  marginBottom: 10,
};

const toggleRow = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  fontSize: '0.88rem',
  marginBottom: 10,
};

const inputStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 8,
  border: '1px solid #ccc',
  background: 'var(--surface)',
  color: 'var(--on-bg)',
  fontSize: '0.9rem',
  marginBottom: 10,
};

const buttonStyle = {
  marginTop: 8,
  padding: '10px 16px',
  background: 'var(--primary)',
  color: '#fff',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.86rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const resetBtnStyle = {
  marginTop: 16,
  padding: '10px 16px',
  background: '#eee',
  color: '#333',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.85rem',
  fontWeight: '500',
  cursor: 'pointer',
};

const statusStyle = {
  marginTop: 10,
  fontSize: '0.85rem',
};

const modalOverlay = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: 'rgba(0,0,0,0.3)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 999,
};

const modalCard = {
  background: 'var(--surface)',
  padding: 24,
  borderRadius: 12,
  boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
  width: '90%',
  maxWidth: 340,
};

const cancelBtnStyle = {
  padding: '10px 16px',
  background: '#eee',
  color: '#333',
  border: 'none',
  borderRadius: 8,
  fontSize: '0.85rem',
  fontWeight: '500',
  cursor: 'pointer',
};
